import React from "react"
import { Link } from "react-router-dom"
import styled from "styled-components"
import { reportAnchorHash } from "../../Common/Utils/reportAnchor"
import { tokenize, type Token } from "./tokenize"

const Bold = styled.strong`
  font-weight: 700;
`

const SourceLink = styled(Link)`
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
`

// Matches a single "Sección N[, Bloque X.Y]" citation, as produced by buildSourceCitation on the backend.
// Groups: section text, section number, separator, block text, block number.
const SOURCE_ENTRY_RE = /(Secci(?:ó|o)n\s+(\d+))(?:(,\s*)(Bloque\s+([\d.]+)))?/i

// A whole line that is (optionally) the "Fuente(s):" label followed by exactly one citation
const CITATION_LINE_RE = /^(?:Fuentes?:\s*)?Secci(?:ó|o)n\s+\d+(?:,\s*Bloque\s+[\d.]+)?$/i

// Each part of the citation links to what it names: "Sección N" to the top of
// the section, "Bloque X.Y" to the block. That opens it whether the reader is on
// the report already or anywhere else. Without the case's report to point at,
// the citation stays plain text.
function renderSourceEntry(text: string, key: number, reportPath?: string): React.ReactNode {
  const match = SOURCE_ENTRY_RE.exec(text)
  if (!match || !reportPath) return <React.Fragment key={key}>{text}</React.Fragment>

  const [, sectionText, sectionNumber, separator, blockText, blockNumber] = match

  return (
    <React.Fragment key={key}>
      <SourceLink to={`${reportPath}${reportAnchorHash(sectionNumber)}`}>{sectionText}</SourceLink>
      {blockNumber && (
        <>
          {separator}
          <SourceLink to={`${reportPath}${reportAnchorHash(sectionNumber, blockNumber)}`}>{blockText}</SourceLink>
        </>
      )}
    </React.Fragment>
  )
}

function renderCitationLine(line: string, key: number, reportPath?: string): React.ReactNode {
  const prefixMatch = /^(Fuentes?:\s*)/i.exec(line)
  const prefix = prefixMatch?.[1] ?? ""
  const entry = prefixMatch ? line.slice(prefixMatch[1].length) : line

  return (
    <React.Fragment key={key}>
      {prefix}
      {renderSourceEntry(entry, key, reportPath)}
    </React.Fragment>
  )
}

const Italic = styled.em`
  font-style: italic;
`

const Strikethrough = styled.span`
  text-decoration: line-through;
`

const InlineCode = styled.code`
  background-color: rgba(0, 0, 0, 0.06);
  border-radius: 3px;
  padding: 0.1em 0.3em;
  font-family: monospace;
  font-size: 0.9em;
`

function renderToken(token: Token, key: number): React.ReactNode {
  switch (token.type) {
    case "bold":
      return <Bold key={key}>{token.content}</Bold>
    case "italic":
      return <Italic key={key}>{token.content}</Italic>
    case "strikethrough":
      return <Strikethrough key={key}>{token.content}</Strikethrough>
    case "code":
      return <InlineCode key={key}>{token.content}</InlineCode>
    default:
      return <React.Fragment key={key}>{token.content}</React.Fragment>
  }
}

interface FormattedTextProps {
  text: string
  reportPath?: string // The report of the case the chat is about, for the chatbot's source links
}

export const FormattedText: React.FC<FormattedTextProps> = ({ text, reportPath }) => {
  const lines = text.split("[BR]")

  return (
    <>
      {lines.map((line, lineIndex) => (
        <React.Fragment key={lineIndex}>
          {CITATION_LINE_RE.test(line)
            ? renderCitationLine(line, lineIndex, reportPath)
            : tokenize(line).map((token, tokenIndex) => renderToken(token, tokenIndex))}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  )
}
